import { ReactNode, useMemo } from 'react';
import { Badge, Tooltip } from '@mui/material';

import { Icon } from '../../shared/Icon';
import { LanguageType } from '@lifeserver/shared';

import { PortalCategoryDTO } from '../../hooks/portal';

const LANGUAGE_ENTRIES = Object.entries(LanguageType) as Array<
    [string, LanguageType]
>;

type Status =
    | { kind: 'ok' }
    | { kind: 'not-localized' }
    | { kind: 'partial'; missingLanguages: string[] };

/**
 * Shows a warning badge on top of `children` (typically a category Avatar)
 * for EventsCalendar categories where the localized name is missing or
 * incomplete. Unity relies on these translations, so missing ones must be
 * visible to admins at a glance.
 */
export const CategoryTranslationStatusBadge = ({
    category,
    translations,
    children,
}: {
    category: PortalCategoryDTO;
    translations: Record<string, string> | undefined;
    children: ReactNode;
}) => {
    const status: Status = useMemo(() => {
        if (category.type !== 'eventsCalendar') {
            return { kind: 'ok' };
        }
        if (!category.portalLocalizationId) {
            return { kind: 'not-localized' };
        }

        const missing = LANGUAGE_ENTRIES.filter(([, code]) => {
            const text = translations?.[code];
            return !text || text.trim().length === 0;
        }).map(([name]) => name);

        if (missing.length === 0) {
            return { kind: 'ok' };
        }
        return { kind: 'partial', missingLanguages: missing };
    }, [category.type, category.portalLocalizationId, translations]);

    if (status.kind === 'ok') {
        return <>{children}</>;
    }

    const tooltipText =
        status.kind === 'not-localized'
            ? 'Localization not set up — Unity will show the original category name'
            : `Missing translations (${status.missingLanguages.length}): ${status.missingLanguages.join(', ')}`;

    return (
        <Tooltip title={tooltipText} arrow>
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                badgeContent={
                    <Icon
                        name="warning"
                        style={{ fontSize: 16, color: '#fff' }}
                    />
                }
                sx={{
                    '& .MuiBadge-badge': {
                        backgroundColor: 'warning.main',
                        minWidth: 20,
                        height: 20,
                        padding: 0,
                        borderRadius: '50%',
                    },
                }}
            >
                {children}
            </Badge>
        </Tooltip>
    );
};
